import { EDisclaimerKeys } from "@/utilities/enums/Disclaimer";
import { E_ENQUIRY_LOG_TYPE } from "@/utilities/enums/EnquiryLogTypes";
import { E_LEAD_SCORE_TYPE } from "@/utilities/enums/LeadScoreTypes";
import { EProjectCategoryTypes } from "@/utilities/enums/ProjectCategory";
import { ESiteContentType } from "@/utilities/enums/SiteContent";
import { ESocialLinks } from "@/utilities/enums/Social";
import { EUnitTransactionStatus } from "@/utilities/enums/UnitTransaction";
import { EUserReferenceType } from "@/utilities/enums/UserPreference";
import { ColorTagColor } from "@/utilities/types/ColorTag";
import { EEnquiryEntryContactStatus } from "@/utilities/types/EnquiryEntry";

export const CART_KEY = "isomorphic-cart";
export const POS_CART_KEY = "isomorphic-pos-cart";
export const DUMMY_ID = "FC6723757651DB74";
export const CHECKOUT = "isomorphic-checkout";
export const CURRENCY_CODE = "USD";
export const LOCALE = "en";
export const LANGUAGE_KEY = "lang";
export const PROJECT_MODAL_SHOWN_KEY = "projectModalShown";
export const AGENCY_PROJECT_MODAL_SHOWN_KEY = "agencyProjectModalShown";
export const CURRENCY_OPTIONS = {
  formation: "en-US",
  fractions: 2,
};

export const ROW_PER_PAGE_OPTIONS = [
  {
    value: 5,
    name: "5",
  },
  {
    value: 10,
    name: "10",
  },
  {
    value: 15,
    name: "15",
  },
  {
    value: 20,
    name: "20",
  },
];

export const ROLES = {
  Administrator: "Administrator",
  Manager: "Manager",
  Sales: "Sales",
  Support: "Support",
  Developer: "Developer",
  HRD: "HR Department",
  RestrictedUser: "Restricted User",
  Customer: "Customer",
} as const;

export const WEB_DOMAIN =
  (import.meta.env.VITE_BASE_WEB_URL as string) ||
  "http://ec2-18-141-58-236.ap-southeast-1.compute.amazonaws.com";

export const SOCIAL_TYPES: Record<ESocialLinks, { label: string }> = {
  [ESocialLinks.FACEBOOK]: { label: "Facebook" },
  [ESocialLinks.INSTAGRAM]: { label: "Instagram" },
  [ESocialLinks.TIKTOK]: { label: "Tiktok" },
};

export const CDN_URL = import.meta.env.VITE_ASSET_BASE_URL as string;

export const DATE_FORMAT = {
  DATE: "MMMM DD, YYYY",
  DATE_TIME: "MMMM DD, YYYY hh:mm A",
  DATE_TIME_SPLICE: "MMMM DD, YYYY. hh:mm A",
  TIME: "hh:mm A",
};

export const PROJECT_CATEGORY_TYPES: Record<
  EProjectCategoryTypes,
  { label: string }
> = {
  [EProjectCategoryTypes.RESIDENTIAL]: { label: "Residential" },
  [EProjectCategoryTypes.COMMERCIAL]: { label: "Commercial" },
};

export const UNIT_TRANSACTION_STATUS: Record<
  EUnitTransactionStatus,
  { label: string }
> = {
  [EUnitTransactionStatus.AVAILABLE]: { label: "Available" },
  [EUnitTransactionStatus.SOLD]: { label: "Sold" },
};

export const USER_PREFERENCE_TYPE: Record<
  EUserReferenceType,
  { label: string }
> = {
  [EUserReferenceType.LOCATION]: { label: "Location" },
  [EUserReferenceType.RANGE]: { label: "Range" },
  [EUserReferenceType.UNIT_TYPE]: { label: "Unit Type" },
};

export const SITE_CONTENT_TYPES: Record<ESiteContentType, { label: string }> = {
  [ESiteContentType.ABOUT_US]: { label: "About us" },
  [ESiteContentType.CCR]: { label: "CCR" },
  [ESiteContentType.CONTACT_US]: { label: "Contact us" },
  [ESiteContentType.OCR]: { label: "OCR" },
  [ESiteContentType.PARTNER_US]: { label: "Partner us" },
  [ESiteContentType.PRIVACT_POLICY]: { label: "Privacy policy" },
  [ESiteContentType.RCR]: { label: "RCR" },
};

export const CLOUDFLARE_TURNSTILE_SITE_KEY = import.meta.env
  .VITE_BASE_CLOUDFLARE_TURNSTILE_SITE_KEY as string;

const RAW_SUFFIX_DOMAIN = import.meta.env.VITE_SUFFIX_DOMAIN as string;

export const SUFFIX_DOMAIN = RAW_SUFFIX_DOMAIN?.startsWith(".")
  ? RAW_SUFFIX_DOMAIN
  : `.${RAW_SUFFIX_DOMAIN}`;

export const LDP_DOMAIN =
  (import.meta.env.VITE_LDP_DOMAIN as string) || "directbooking.sg";

const ENV_LDP_DOMAINS_LIST = (
  import.meta.env.VITE_LDP_DOMAINS_LIST as string
)?.split(",");

export const LDP_DOMAINS_LIST = ENV_LDP_DOMAINS_LIST || [LDP_DOMAIN];

export const DISCLAIMERS: Record<EDisclaimerKeys, { label: string }> = {
  [EDisclaimerKeys.Disclaimer]: { label: "Project Disclaimer" },
  [EDisclaimerKeys.FooterDisclaimer]: { label: "Footer Disclaimer" },
  [EDisclaimerKeys.FormNotice]: { label: "Form Notice" },
};

export const ENQUIRY_ENTRY_CONTACT_STATUS = [
  {
    value: EEnquiryEntryContactStatus.NEW,
    label: "New",
  },
  {
    value: EEnquiryEntryContactStatus.OPEN,
    label: "Open",
  },
  {
    value: EEnquiryEntryContactStatus.IN_PROGRESS,
    label: "In Progress",
  },
  {
    value: EEnquiryEntryContactStatus.UNQUALIFIED,
    label: "Unqualified",
  },
  {
    value: EEnquiryEntryContactStatus.ATTEMPTED_TO_CONTACT,
    label: "Attempted to Contact",
  },
  {
    value: EEnquiryEntryContactStatus.CONNECTED,
    label: "Connected",
  },
];

export const LEAD_SCORE_COLORS = {
  AGENT_DUPLICATE: "rgba(146, 146, 146, 1)", // Grey
  COLD: "rgba(238, 0, 0, 1)", // Red
  WARM: "rgba(245, 166, 35, 1)", // Orange
  HOT: "rgba(17, 168, 73, 1)", // Green
};

export const LEAD_SCORE_RIZZUI_COLORS = {
  AGENT_DUPLICATE: "primary", // Grey
  COLD: "danger", // Red
  WARM: "warning", // Orange
  HOT: "success", // Green
};

export const LEAD_FULL_SCORE = 80;

export const ENQUIRY_LOG_TYPES: Record<E_ENQUIRY_LOG_TYPE, { label: string }> =
  {
    [E_ENQUIRY_LOG_TYPE.APPOINTMENT_DATE]: { label: "Appointment Urgency" },
    [E_ENQUIRY_LOG_TYPE.INTERESTED]: { label: "Interested Items" },
    [E_ENQUIRY_LOG_TYPE.PROJECT_VIEW_COUNT]: { label: "Viewed Projects" },
    [E_ENQUIRY_LOG_TYPE.PROJECT_VIEW_DURATION]: {
      label: "Viewing Duration",
    },
    [E_ENQUIRY_LOG_TYPE.RED_FLAG]: { label: "Duplicate Submission Detected" },
    [E_ENQUIRY_LOG_TYPE.UNIT_TYPE]: { label: "Unit Types" },
    [E_ENQUIRY_LOG_TYPE.HAVE_CEA_INFO]: { label: "Agent Detected" },
  };

export const LEAD_SCORE_TYPES: Record<
  E_LEAD_SCORE_TYPE,
  { label: string; color: string; rizzuiColor: string }
> = {
  [E_LEAD_SCORE_TYPE.AGENT_DUPLICATE]: {
    label: "Agent/Duplicate",
    color: LEAD_SCORE_COLORS.AGENT_DUPLICATE,
    rizzuiColor: LEAD_SCORE_RIZZUI_COLORS.AGENT_DUPLICATE,
  },
  [E_LEAD_SCORE_TYPE.COLD]: {
    label: "Cold",
    color: LEAD_SCORE_COLORS.COLD,
    rizzuiColor: LEAD_SCORE_RIZZUI_COLORS.COLD,
  },
  [E_LEAD_SCORE_TYPE.HOT]: {
    label: "Hot",
    color: LEAD_SCORE_COLORS.HOT,
    rizzuiColor: LEAD_SCORE_RIZZUI_COLORS.HOT,
  },
  [E_LEAD_SCORE_TYPE.WARM]: {
    label: "Warm",
    color: LEAD_SCORE_COLORS.WARM,
    rizzuiColor: LEAD_SCORE_RIZZUI_COLORS.WARM,
  },
};

export const COUNTRY_SG_CODE = "+65";

export const APP_SUPPORT_DOMAIN =
  import.meta.env.VITE_APP_SUPPORT_DOMAIN ||
  "app-development.staging.project.sg";

export const LDP_SUBDOMAINS = [...LDP_DOMAINS_LIST.map((d) => `.${d}`)];

export const PAYMENT_CUSTOM_SIZE = "650px";

export const NOTIFICATION_REFRESH: number =
  Number(import.meta.env.NOTIFICATION_REFRESH) || 900000;

export const GOOGLE_OAUTH_URL = import.meta.env.VITE_GOOGLE_OAUTH_URL as string;

export const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string;

export const GOOGLE_REDIRECT_URI = import.meta.env
  .VITE_GOOGLE_REDIRECT_URI as string;

export const GOOGLE_RESPONSE_TYPE = import.meta.env
  .VITE_GOOGLE_RESPONSE_TYPE as string;

export const GOOGLE_SCOPE = import.meta.env.VITE_GOOGLE_SCOPE as string;

export const GOOGLE_ACCESS_TYPE = import.meta.env
  .VITE_GOOGLE_ACCESS_TYPE as string;

export const GOOGLE_PROMPT = import.meta.env.VITE_GOOGLE_PROMPT as string;

export const MOBILE_BREAKPOINT = 768 as const;

export const WHATSAPP_US_LINK = "https://wa.link/ivhh5n" as const;

export const DEFAULT_REASON_CREDIT = {
  ADD: "Manual Credit Top-up",
  DEDUCT: "Ad Spend Deduction",
};

// Predefined Mac OS style colors with their corresponding CSS classes and hex codes
export const COLOR_TAG_STYLES: Record<
  ColorTagColor,
  { bg: string; text: string; border: string; hex: string }
> = {
  [ColorTagColor.RED]: {
    bg: "bg-red-100",
    text: "text-red-800",
    border: "border-red-200",
    hex: "#FEE2E2",
  },
  [ColorTagColor.ORANGE]: {
    bg: "bg-orange-100",
    text: "text-orange-800",
    border: "border-orange-200",
    hex: "#FFEDD5",
  },
  [ColorTagColor.YELLOW]: {
    bg: "bg-yellow-100",
    text: "text-yellow-800",
    border: "border-yellow-200",
    hex: "#FEF3C7",
  },
  [ColorTagColor.GREEN]: {
    bg: "bg-green-100",
    text: "text-green-800",
    border: "border-green-200",
    hex: "#DCFCE7",
  },
  [ColorTagColor.BLUE]: {
    bg: "bg-blue-100",
    text: "text-blue-800",
    border: "border-blue-200",
    hex: "#DBEAFE",
  },
  [ColorTagColor.PURPLE]: {
    bg: "bg-purple-100",
    text: "text-purple-800",
    border: "border-purple-200",
    hex: "#E9D5FF",
  },
  [ColorTagColor.PINK]: {
    bg: "bg-pink-100",
    text: "text-pink-800",
    border: "border-pink-200",
    hex: "#FCE7F3",
  },
  [ColorTagColor.GRAY]: {
    bg: "bg-gray-100",
    text: "text-gray-800",
    border: "border-gray-200",
    hex: "#F3F4F6",
  },
};
